import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Bell, BellRing, X, CheckCheck, Trash2,
  Flame, Sparkles, Moon, Cloud, Star, Heart,
  Settings, ChevronRight
} from "lucide-react";
import {
  getInAppNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification
} from "../utils/pushNotifications";
import { formatDistanceToNow } from "date-fns";

const NOTIFICATION_ICONS = {
  streak_at_risk: { icon: Flame, color: "text-orange-500", bg: "bg-orange-500/10" },
  miracle_card: { icon: Sparkles, color: "text-purple-500", bg: "bg-purple-500/10" },
  moon_phase: { icon: Moon, color: "text-blue-400", bg: "bg-blue-400/10" },
  dream_journal: { icon: Cloud, color: "text-indigo-400", bg: "bg-indigo-400/10" },
  collective_intention: { icon: Star, color: "text-amber-500", bg: "bg-amber-500/10" },
  partner_love: { icon: Heart, color: "text-pink-500", bg: "bg-pink-500/10" },
  default: { icon: Bell, color: "text-muted-foreground", bg: "bg-muted" }
};

// Where to send users when they tap a notification. These deeplink into
// /dashboard?open=<modal> so the modal auto-opens — otherwise users land on
// the dashboard wondering where their miracle card went.
const NOTIFICATION_DESTINATIONS = {
  streak_at_risk: "/dashboard",
  miracle_card: "/dashboard?open=miracle-deck",
  manifestation_card: "/dashboard?open=miracle-deck",
  moon_phase: "/dashboard?open=moon-cycle",
  full_moon: "/dashboard?open=moon-cycle",
  new_moon: "/dashboard?open=moon-cycle",
  dream_journal: "/dreams",
  collective_intention: "/dashboard",
  partner_love: "/soul-circle",
  weekly_report: "/dashboard?open=weekly-report",
  vibe_out: "/dashboard?open=vibe-out",
};

function destinationFor(notification) {
  // Server can pass an explicit `link` or `data.url` — honor it first
  return (
    notification?.link
    || notification?.data?.url
    || NOTIFICATION_DESTINATIONS[notification?.type]
    || "/dashboard"
  );
}

const NotificationItem = ({ notification, onActivate, onDelete }) => {
  const config = NOTIFICATION_ICONS[notification.type] || NOTIFICATION_ICONS.default;
  const Icon = config.icon;

  const timeAgo = notification.created_at
    ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
    : '';

  return (
    <div className={`group relative border-b border-border/50 ${!notification.read ? 'bg-primary/5' : ''}`}>
      {/* Real <button> — most reliable click target on iOS PWAs.
          The previous <div onClick> sometimes wouldn't fire on iOS Safari
          even with cursor-pointer, especially inside a Radix ScrollArea. */}
      <button
        type="button"
        onClick={() => onActivate(notification)}
        className="w-full text-left p-3 pr-10 transition-colors hover:bg-muted/50 active:bg-muted/70 focus:outline-none focus:bg-muted/40"
        data-testid={`notification-item-${notification.id}`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${config.bg}`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-medium truncate ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                {notification.title}
              </p>
              {!notification.read && (
                <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" aria-label="Unread" />
              )}
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
              {notification.body}
            </p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-muted-foreground">{timeAgo}</p>
              <span className="text-[10px] text-primary/80 font-medium flex items-center gap-0.5">
                Open <ChevronRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </div>
      </button>

      {/* Trash sits OUTSIDE the activation button so its tap can't bubble. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 transition"
        aria-label="Delete notification"
        data-testid={`notification-delete-${notification.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const NotificationCenter = ({ onOpenSettings }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false); // toggle: latest 5 vs full list
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen && token) {
      fetchNotifications();
    }
  }, [isOpen, token]);

  // Close dropdown when tapping/clicking outside.
  // Listen for BOTH mousedown (desktop) AND touchstart (iOS/Android) — the
  // previous version only listened for mousedown which meant the panel
  // wouldn't release on mobile taps. Also close on Escape.
  useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const fetchUnreadCount = async () => {
    try {
      const count = await getUnreadCount(token);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      // Pull plenty so the "Show all" toggle has something to expand to.
      const data = await getInAppNotifications(token, 50, false);
      setNotifications(data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tap on a notification → mark as read, close panel, navigate to the
  // relevant area of the app. (Was: just marked as read silently — felt
  // unresponsive, hence the user complaint that "they aren't going anywhere".)
  const handleActivate = async (notification) => {
    try {
      if (!notification.read) {
        await markNotificationRead(token, notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    } finally {
      setIsOpen(false);
      navigate(destinationFor(notification));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead(token);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(token, notificationId);
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === notificationId);
        if (target && !target.read) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
        return prev.filter((n) => n.id !== notificationId);
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const visibleNotifications = showAll ? notifications : notifications.slice(0, 5);
  const hasMore = notifications.length > 5;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        {unreadCount > 0 && (
          <Badge
            className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center text-[10px] bg-primary"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <Card
          className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-16 sm:top-12 w-auto sm:w-96 shadow-xl z-50 max-h-[70vh] sm:max-h-[80vh] flex flex-col"
          data-testid="notification-dropdown"
        >
          <CardHeader className="pb-2 border-b px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base">Notifications</CardTitle>
              <div className="flex items-center gap-0.5 sm:gap-1">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[10px] sm:text-xs h-7 sm:h-8 px-2"
                    onClick={handleMarkAllRead}
                    data-testid="notification-mark-all-read"
                  >
                    <CheckCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                    <span className="hidden xs:inline">Mark all</span>
                    <span className="xs:hidden">All</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-7 h-7 sm:w-8 sm:h-8"
                  onClick={() => { setIsOpen(false); onOpenSettings?.(); }}
                  data-testid="notification-settings-btn"
                >
                  <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-7 h-7 sm:w-8 sm:h-8"
                  onClick={() => setIsOpen(false)}
                  data-testid="notification-close-btn"
                  aria-label="Close notifications"
                >
                  <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>

          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch", maxHeight: "60vh" }}
            data-testid="notification-scroll"
          >
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <div className="animate-pulse">Loading...</div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">We'll let you know when something happens</p>
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {visibleNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onActivate={handleActivate}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer: "Show all / Show less" toggle. Hidden when ≤5 notifications. */}
          {hasMore && (
            <CardContent className="p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => setShowAll((s) => !s)}
                data-testid="notification-show-all-toggle"
              >
                {showAll
                  ? `Show recent (${notifications.length - 5} hidden)`
                  : `Show all (${notifications.length})`}
              </Button>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default NotificationCenter;
